import { isBeautifulLinkBasedOnId } from './beautifiedPrefix';
import { SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME } from './constants';
import getSharingInfoToTryUpdateLink from './getSharingInfoToTryUpdateLink';
import shouldProcessLink from './shouldProcessLink';
import trySetIdAndClassName from './trySetIdAndClassName';
import LinkPlugin from '../plugin/LinkPlugin';
import { logUsage } from 'owa-analytics';
import { AttachmentWellViewState } from 'owa-attachment-well-data';
import { isSharePointLink } from 'owa-sharepoint-link-detector';

export interface SharePointLink {
    id: string;
    url: string;
    anchorElement: HTMLAnchorElement;
}

export enum AnchorElementsSource {
    // User pastes html
    PasteHtml,

    // Loading from draft, forward, reply
    ContentChangedFromSetContent,
}

export default async function processAnchorElementsFromHtmlContent(
    allLinks: NodeListOf<Element> | Element[],
    hiddenReferenceAttachments: NodeListOf<Element> | Element[],
    source: AnchorElementsSource,
    linkPlugin: LinkPlugin,
    viewState: AttachmentWellViewState,
    linkIdsWithSpinner: string[],
    shouldChangeLinkId: boolean,
    shouldTryBeautify: boolean = false
) {
    // We need to set id and className for all sharePoint links so that getAllSharePointLinks can find them.
    const sharePointLinks: SharePointLink[] = [];
    for (let i = 0; i < allLinks.length; i++) {
        const link = allLinks[i] as HTMLAnchorElement;
        if (shouldProcessLink(link, hiddenReferenceAttachments)) {
            const url = getUrl(link);
            if (!isSharePointLink(url)) {
                continue;
            }

            trySetIdAndClassName(link, shouldChangeLinkId);
            sharePointLinks.push({ id: link.id, url: link.href, anchorElement: link });
        }
    }

    if (sharePointLinks.length < 10) {
        logUsage('UnderTenSPLinksWhenProcessAnchorElements', [
            source === AnchorElementsSource.PasteHtml,
            sharePointLinks.length,
        ]);
    } else {
        logUsage('AtLeastTenSPLinksWhenProcessAnchorElements', [
            source === AnchorElementsSource.PasteHtml,
        ]);
    }

    for (let i = 0; i < sharePointLinks.length; i++) {
        const sharePointLink = sharePointLinks[i];
        const isLinkPreviouslyBeautified: boolean = isBeautifulLinkBasedOnId(
            sharePointLink.anchorElement
        );
        const isLinkPureAnchorElement: boolean = isPureAnchorElement(sharePointLink.anchorElement);
        const shouldBeautify: boolean =
            isEligibleForBeautifyLink(sharePointLink.anchorElement) &&
            isLinkPureAnchorElement &&
            shouldTryBeautify; // We only try to beautify for some links from paste
        const shouldRehydrate: boolean = isLinkPreviouslyBeautified || isLinkPureAnchorElement;
        logUsageForLinks(
            source,
            isLinkPreviouslyBeautified,
            isLinkPureAnchorElement,
            shouldBeautify
        );
        getSharingInfoToTryUpdateLink(
            linkPlugin,
            shouldBeautify,
            shouldRehydrate,
            isLinkPreviouslyBeautified,
            sharePointLink.anchorElement,
            viewState,
            i,
            linkIdsWithSpinner
        );
    }
}

function logUsageForLinks(
    source: AnchorElementsSource,
    isLinkPreviouslyBeautified: boolean,
    isLinkPureAnchorElement: boolean,
    shouldBeautify: boolean
) {
    let linkType: string = '';
    if (shouldBeautify) {
        linkType = 'shouldBeautify';
    } else if (isLinkPreviouslyBeautified) {
        linkType = 'isLinkPreviouslyBeautified';
    } else if (isLinkPureAnchorElement) {
        linkType = 'isLinkPureAnchorElement';
    }
    if (source === AnchorElementsSource.PasteHtml) {
        logUsage('ComposeSPLinksFromPaste', [linkType]);
    } else if (source === AnchorElementsSource.ContentChangedFromSetContent) {
        logUsage('ComposeSPLinksFromDraft', [linkType]);
    }
}

export function getUrl(link: HTMLAnchorElement): string {
    const url: string = link.getAttribute(SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME);
    if (url && url.length > 0) {
        return url;
    }

    return link.href;
}

export function isEligibleForBeautifyLink(link: HTMLAnchorElement): boolean {
    let url: string = link.getAttribute(SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME);
    if (!url) {
        url = link.href;
    }

    return url.toLowerCase() === link.text.toLowerCase();
}

function isPureAnchorElement(link: HTMLAnchorElement): boolean {
    return !link.children || link.children.length === 0;
}
